import {
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { TimeOffTypeCodes } from '../../../models/time-off.models';

/** One time-off type's editable allotments (default days + max carryover). */
export interface TimeOffPolicyEditRow {
  typeCode: string;
  typeName: string;
  defaultDays: number;
  maxCarryoverDays: number | null;
}

/**
 * One practice area's Vacation override as edited in the dialog. Lives in the working model, not
 * the table DOM, so filtering and paging never lose an edit.
 */
export interface PracticeAreaOverrideEditRow {
  practiceAreaCode: string;
  practiceAreaName: string;
  /** Existing override policy id; null when this practice area follows the office default today. */
  policyId: number | null;
  /** Toggle state. When false the two values below are ignored by the caller. */
  enabled: boolean;
  /** Vacation days when enabled. null = blank input, which is invalid while enabled. */
  defaultDays: number | null;
  /** Max carryover when enabled. null = no cap (carry over everything). */
  maxCarryoverDays: number | null;
}

/** Seeds the Time-Off Policies dialog with the office's current allotments. */
export interface TimeOffPoliciesDialogData {
  officeName: string;
  policies: TimeOffPolicyEditRow[];
  /**
   * One row per firm practice area, prefilled and enabled where a Vacation override already
   * exists, plus any orphaned override whose code is no longer a known practice area.
   */
  practiceAreaOverrides: PracticeAreaOverrideEditRow[];
}

/** What the dialog hands back on Save; the caller diffs both lists against what it loaded. */
export interface TimeOffPoliciesDialogResult {
  policies: TimeOffPolicyEditRow[];
  practiceAreaOverrides: PracticeAreaOverrideEditRow[];
}

/**
 * Configure-all dialog for an office's time-off policies. Edits every type's default days and
 * max carryover, plus per-practice-area Vacation overrides, in one form; returns the edited rows
 * on Save so the caller can persist only what changed. Makes no HTTP calls of its own.
 */
@Component({
  selector: 'app-time-off-policies-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSlideToggleModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './time-off-policies-dialog.component.html',
  styleUrls: ['./time-off-policies-dialog.component.scss'],
})
export class TimeOffPoliciesDialogComponent {
  private dialogRef =
    inject<
      MatDialogRef<TimeOffPoliciesDialogComponent, TimeOffPoliciesDialogResult>
    >(MatDialogRef);
  data = inject<TimeOffPoliciesDialogData>(MAT_DIALOG_DATA);

  /** Editable working copy so Cancel discards changes. */
  policies: TimeOffPolicyEditRow[] = this.data.policies.map((p) => ({ ...p }));

  /**
   * Editable working copy of the override rows. Sorted once at open — enabled first, then by
   * name — so existing overrides are visible without scrolling. Never re-sorted live: a row that
   * jumped to the top the moment its toggle flipped would move out from under the pointer.
   */
  overrides: PracticeAreaOverrideEditRow[] = sortOverrideRows(
    this.data.practiceAreaOverrides.map((o) => ({ ...o })),
  );

  /** Backs the paginated override table (filtering and paging are client-side). */
  readonly overridesDataSource =
    new MatTableDataSource<PracticeAreaOverrideEditRow>(this.overrides);

  readonly overrideColumns = [
    'practiceArea',
    'override',
    'defaultDays',
    'maxCarryoverDays',
  ];

  /**
   * Whether the practice-area override section is open. Starts open only when the office already
   * has overrides, so those are visible without a hunt; an office with none opens collapsed and
   * the dialog fits without a scrollbar. Read from the injected data rather than
   * `enabledOverrideCount` so it does not depend on field-initialisation order.
   */
  overridesExpanded = this.data.practiceAreaOverrides.some((o) => o.enabled);

  /** Free-text filter for the override table. */
  overrideFilter = '';

  readonly overridePageSize = 5;

  readonly overridePageSizeOptions = [5, 10, 25];

  private readonly overridePaginator = viewChild(MatPaginator);

  /** The dialog's scrolling body, so opening the override section can bring it into view. */
  private readonly policiesContent =
    viewChild<ElementRef<HTMLElement>>('policiesContent');

  private readonly injector = inject(Injector);

  /**
   * Practice-area codes whose values are already meaningful — either the row arrived as a saved
   * override, or an earlier enable seeded it. Seeding is once per row, not per empty field,
   * because `maxCarryoverDays: null` is ambiguous: on a saved override it means "no cap", a real
   * setting, and on a fresh row it just means empty. Keying off the field alone would let a
   * re-enable quietly reimpose the office's cap over a deliberate "no cap".
   */
  private readonly seededOverrideCodes = new Set<string>(
    this.data.practiceAreaOverrides
      .filter((o) => o.enabled)
      .map((o) => o.practiceAreaCode.toLowerCase()),
  );

  /**
   * Flags a day input once the user has touched it and its value is not a whole number of 0 or
   * more. Reads the control's own value, so one matcher serves every field that holds a day
   * count — the office defaults and the per-practice-area override rows alike.
   */
  readonly daysErrorMatcher: ErrorStateMatcher = {
    isErrorState: (control: FormControl | null): boolean =>
      hasInteracted(control) && !isNonNegativeInteger(control!.value),
  };

  /** Same, for the carry-over cap — where blank is legitimate and means "no cap". */
  readonly carryoverErrorMatcher: ErrorStateMatcher = {
    isErrorState: (control: FormControl | null): boolean =>
      hasInteracted(control) &&
      control!.value !== null &&
      !isNonNegativeInteger(control!.value),
  };

  constructor() {
    // Assign unconditionally: attaching a new paginator instance and releasing a destroyed one are
    // the same operation, and the data source must not stay merged onto a dead paginator.
    effect(() => {
      this.overridesDataSource.paginator = this.overridePaginator() ?? null;
    });

    // Match on the name and code only. The default predicate stringifies every property, so
    // "true" would filter to the enabled rows and a number would match the day counts.
    this.overridesDataSource.filterPredicate = (row, filterText) =>
      row.practiceAreaName.toLowerCase().includes(filterText) ||
      row.practiceAreaCode.toLowerCase().includes(filterText);

    // The parent opens this with disableClose, so a stray backdrop click cannot throw away a
    // part-filled form. That switch also swallows ESC, which was never the point — route it
    // back to Cancel so the keyboard exit still works.
    this.dialogRef
      .keydownEvents()
      .pipe(
        filter((event) => event.key === 'Escape'),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.cancel());
  }

  /** How many practice areas currently have an override switched on. */
  get enabledOverrideCount(): number {
    return this.overrides.filter((o) => o.enabled).length;
  }

  /**
   * Enabled override rows that are not yet well-formed. `canSave` has to consider every row,
   * including ones the filter or the paginator has hidden — writing a half-filled row would be
   * worse than blocking Save — so the template surfaces this list to explain a disabled button
   * the user would otherwise have no way to account for.
   */
  get invalidOverrideRows(): PracticeAreaOverrideEditRow[] {
    return this.overrides.filter((row) => !this.isOverrideRowValid(row));
  }

  /** Name (or code) of the first row blocking Save, for the message beside the button. */
  get firstInvalidOverrideLabel(): string {
    const first = this.invalidOverrideRows[0];
    return first ? first.practiceAreaName || first.practiceAreaCode : '';
  }

  /**
   * Bring the first offending row into view: open the section, then filter to it. Both steps are
   * needed — the section can be collapsed, and even open the row can be sitting on a later page.
   */
  showInvalidOverrides(): void {
    const label = this.firstInvalidOverrideLabel;
    if (!label) return;

    if (!this.overridesExpanded) {
      this.expandOverrides();
    }
    this.onOverrideFilterChange(label);
  }

  /**
   * Save is allowed only when every row that will be written is well-formed. Office-default rows
   * need a whole-number day count; an enabled override needs one too, and a cap that is either
   * blank (no cap) or a whole number.
   */
  get canSave(): boolean {
    return (
      this.policies.every(
        (p) =>
          isNonNegativeInteger(p.defaultDays) &&
          (p.maxCarryoverDays === null ||
            isNonNegativeInteger(p.maxCarryoverDays)),
      ) && this.overrides.every((row) => this.isOverrideRowValid(row))
    );
  }

  /**
   * Open or close the override section. Opening also scrolls the dialog body down, because the
   * section is the last thing in it: without this the table unfolds below the fold and the admin
   * has to scroll to reach the control they just asked to see. Deliberately not applied to the
   * auto-expand above — jumping past the office defaults before the admin has touched anything
   * would hide the fields they are most likely to have come for.
   */
  toggleOverridesExpanded(): void {
    if (this.overridesExpanded) {
      this.overridesExpanded = false;
      return;
    }
    this.expandOverrides();
  }

  private expandOverrides(): void {
    this.overridesExpanded = true;

    // The table only exists once the @if re-renders, so the scroll has to wait for that — run
    // any earlier and scrollHeight is still the collapsed height, making this a no-op.
    afterNextRender(
      () => {
        const el = this.policiesContent()?.nativeElement;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      },
      { injector: this.injector },
    );
  }

  onOverrideFilterChange(value: string): void {
    this.overrideFilter = value;
    this.overridesDataSource.filter = value.trim().toLowerCase();
    this.overridePaginator()?.firstPage();
  }

  clearOverrideFilter(): void {
    this.onOverrideFilterChange('');
  }

  /**
   * Flip a practice area's override on or off. Switching on seeds an empty field from the office's
   * Vacation policy so the admin edits from the current numbers rather than from blanks. Switching
   * off leaves the values in place — re-enabling restores them, and the caller ignores them while
   * `enabled` is false.
   */
  toggleOverride(row: PracticeAreaOverrideEditRow, enabled: boolean): void {
    row.enabled = enabled;
    if (!enabled) return;

    // Seed only the first time this row is switched on. Once it holds values — saved ones or
    // ones the admin typed — they are theirs, so clearing a field and toggling the row cannot
    // cost them their entry, and a saved "no cap" is never overwritten by the office's cap.
    const key = row.practiceAreaCode.toLowerCase();
    if (this.seededOverrideCodes.has(key)) return;
    this.seededOverrideCodes.add(key);

    const vacation = this.policies.find(
      (p) => p.typeCode === TimeOffTypeCodes.VACATION,
    );
    row.defaultDays = vacation?.defaultDays ?? 0;
    row.maxCarryoverDays = vacation?.maxCarryoverDays ?? null;
  }

  /** A disabled row is always valid: its values are never written. */
  isOverrideRowValid(row: PracticeAreaOverrideEditRow): boolean {
    if (!row.enabled) {
      return true;
    }
    return (
      isNonNegativeInteger(row.defaultDays) &&
      (row.maxCarryoverDays === null ||
        isNonNegativeInteger(row.maxCarryoverDays))
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (!this.canSave) {
      return;
    }
    this.dialogRef.close({
      policies: this.policies,
      practiceAreaOverrides: this.overrides,
    });
  }
}

/** Enabled rows first, then alphabetical by name (falling back to the code). */
function sortOverrideRows(
  rows: PracticeAreaOverrideEditRow[],
): PracticeAreaOverrideEditRow[] {
  return [...rows].sort((a, b) => {
    if (a.enabled !== b.enabled) {
      return a.enabled ? -1 : 1;
    }
    return (a.practiceAreaName || a.practiceAreaCode).localeCompare(
      b.practiceAreaName || b.practiceAreaCode,
    );
  });
}

/**
 * `type="number"` inputs bound with ngModel yield null for a blank field and can yield a
 * fractional or negative value, none of which the API accepts for a day count.
 */
function isNonNegativeInteger(
  value: number | null | undefined,
): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/** Only flag a field once the user has actually been in it. */
function hasInteracted(control: FormControl | null): control is FormControl {
  return !!control && (control.touched || control.dirty);
}
